/**
 * Box 2: six scorecards with Δ% against the immediately preceding window of
 * the same length (spec §3.3, §4). Tones come from the one D1 map (lookerDelta):
 * CTR up = good, CPC / CPM up = bad, Spend / Impressions / Clicks neutral. Pure.
 *
 * @module services/overview/scorecards
 */
import { deltaOf, deltaTone } from '@/services/looker/lookerDelta';
import { cpcOf, cpmOf, ctrOf, isLowSample } from './overviewMath';
import type { Totals } from './overviewTypes';

export type ScoreMetric = 'spend' | 'impressions' | 'clicks' | 'ctr' | 'cpc' | 'cpm';
export const SCORE_METRICS: readonly ScoreMetric[] = ['spend', 'impressions', 'clicks', 'ctr', 'cpc', 'cpm'];

export interface Scorecard {
  metric: ScoreMetric;
  value: number | null;
  previous: number | null;
  /** null when the previous value is null or 0. */
  delta: number | null;
  tone: 'good' | 'bad' | 'neutral';
  /** R17: a ratio card over fewer than 500 impressions. */
  lowSample: boolean;
}

const RATIOS: ReadonlySet<ScoreMetric> = new Set(['ctr', 'cpc', 'cpm']);

const valueOf = (m: ScoreMetric, t: Totals): number | null => {
  if (m === 'ctr') return ctrOf(t);
  if (m === 'cpc') return cpcOf(t);
  if (m === 'cpm') return cpmOf(t);
  return t[m];
};

export const scorecardsOf = (current: Totals, previous: Totals): Scorecard[] =>
  SCORE_METRICS.map((metric) => {
    const value = valueOf(metric, current);
    const prev = valueOf(metric, previous);
    const delta = deltaOf(value, prev);
    return { metric, value, previous: prev, delta, tone: deltaTone(metric, delta), lowSample: RATIOS.has(metric) && isLowSample(current) };
  });
